import React, { useState, useMemo, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Modal, Button, Form, Collapse } from 'react-bootstrap';
import { getDatabase, ref, set } from 'firebase/database';
import { Plus, ChevronDown, ChevronLeft, ChevronRight, Clock, Wrench, Settings, AlertTriangle } from 'lucide-react';
import { useIsSmallScreen } from '../../hooks/useIsSmallScreen';
import { recordActivity } from '../../api/activityApi';
import { useBuildComponentTypes } from '../../contexts/BuildComponentTypesContext';
import { useMaterials } from '../../contexts/MaterialsContext';
import { BaseType, OutdoorCategory, UtilityCategory } from '../../types/Enums';
import { DoorCategory } from '../../types/protocols';
import { translate } from '../../types/EnumTranslations';
import { RELEVANT_BASE_TYPES } from '../../Helpers/baseTypeUtils';
import { getBaseTypeIconPath } from '../../Helpers/componentTypeIconUtils';
import { generateSlug } from '../../Helpers/slugUtils';
import { buildComponentTypeDisplayName } from '../../Helpers/windowTypeName';
import ComponentTypeIcon from '../../Components/ComponentTypeIcon';
import ComponentTypeDetailPane from './ComponentTypeDetailPane';
import ConditionAssessmentTab from './ConditionAssessmentTab';
import './ComponentTypesManagementPage.css';
import { isTemplateArchived } from '../../Helpers/maintenanceTemplates';

/**
 * Generate a short random suffix for uniqueness (4 chars).
 */
const generateShortId = (): string => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 4; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
};

/**
 * Validate a custom key - must be lowercase alphanumeric with underscores only.
 */
const isValidCustomKey = (key: string): boolean => {
    return /^[a-z0-9_]+$/.test(key) && key.length > 0 && key.length <= 50;
};

/**
 * Component Types Management Page
 *
 * Replaces MaintenanceTemplatesEditor with a split-pane layout for managing
 * config/buildComponentTypes. Supports creating, editing, and deleting component types.
 *
 * Key format for custom types: {baseType}_{slug}_{shortId} (auto-generated)
 * or {baseType}_{customKey} (manually specified)
 */
const ComponentTypesManagementPage: React.FC = () => {
    const { types, getTranslation, getTemplates, refresh } = useBuildComponentTypes();
    const { materials } = useMaterials();

    // Window types show their materials and pane count after the name
    const getDisplayName = useCallback((typeKey: string): string => {
        const definition = types[typeKey];
        return (definition && buildComponentTypeDisplayName(definition, (materialKey) => materials[materialKey]?.translations?.sv))
            || getTranslation(typeKey);
    }, [types, materials, getTranslation]);
    const [searchParams, setSearchParams] = useSearchParams();

    // Refresh component types when navigating to this page
    useEffect(() => {
        refresh();
    }, [refresh]);

    // Tab state; `?tab=assessment` (from Konfigurationsstatus) opens the Skickbedömning tab
    const [activeTab, setActiveTab] = useState<string>(() => searchParams.get('tab') === 'assessment' ? 'assessment' : 'types');

    // Selection state from URL search params (validated against loaded types)
    const typeFromUrl = searchParams.get('type');
    const selectedTypeKey = typeFromUrl && types[typeFromUrl] ? typeFromUrl : null;

    // Update URL when selecting a type
    const setSelectedTypeKey = useCallback((typeKey: string | null) => {
        setSearchParams(prev => {
            if (typeKey) {
                prev.set('type', typeKey);
            } else {
                prev.delete('type');
            }
            return prev;
        }, { replace: true });
    }, [setSearchParams]);

    // Phones show either the type list or the open type, never both. Going
    // back returns the list to where it was scrolled.
    const isSmallScreen = useIsSmallScreen();
    const mobileView = isSmallScreen && selectedTypeKey ? 'detail' : 'list';
    const typesPageRef = useRef<HTMLDivElement>(null);
    const listScrollTopRef = useRef(0);

    useLayoutEffect(() => {
        if (!isSmallScreen) return;
        const scroller = typesPageRef.current?.closest('.main-content');
        if (!scroller) return;
        scroller.scrollTop = mobileView === 'list' ? listScrollTopRef.current : 0;
    }, [mobileView, isSmallScreen]);

    const rememberListScroll = () => {
        listScrollTopRef.current = typesPageRef.current?.closest('.main-content')?.scrollTop ?? 0;
    };

    // Clear invalid type from URL if it doesn't exist in types
    useEffect(() => {
        if (typeFromUrl && !types[typeFromUrl] && Object.keys(types).length > 0) {
            setSelectedTypeKey(null);
        }
    }, [typeFromUrl, types, setSelectedTypeKey]);

    // Track if we need to scroll to selected type (only on initial load from URL)
    const hasScrolledToSelection = useRef(false);
    const pendingScrollType = useRef<string | null>(typeFromUrl);

    // Collapsed sections state (tracks which sections are collapsed)
    const [collapsedSections, setCollapsedSections] = useState<Set<BaseType>>(new Set());

    // Add new type modal state
    const [showAddModal, setShowAddModal] = useState(false);
    const [newTypeName, setNewTypeName] = useState('');
    const [newTypeBaseType, setNewTypeBaseType] = useState<BaseType | null>(null);
    const [newTypeCategory, setNewTypeCategory] = useState<string>('');
    const [isAdding, setIsAdding] = useState(false);
    const [addError, setAddError] = useState<string | null>(null);

    // Special baseType props for windows/doors
    const [newTypePaneCount, setNewTypePaneCount] = useState<number | null>(null);
    const [newTypeDoorCategory, setNewTypeDoorCategory] = useState<string>('');

    // Advanced settings for custom key
    const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
    const [customKeySuffix, setCustomKeySuffix] = useState('');

    // Check if selected base type requires a category
    const requiresCategory = newTypeBaseType === BaseType.Utility || newTypeBaseType === BaseType.Outdoor;

    // Ref for the scrollable list container
    const listContainerRef = useRef<HTMLDivElement>(null);

    // Use base types in order defined by RELEVANT_BASE_TYPES (matches BuildingsPage button layout)
    const sortedBaseTypes = RELEVANT_BASE_TYPES;

    // Group types by baseType for display
    const typesByBaseType = useMemo(() => {
        const grouped: Partial<Record<BaseType, string[]>> = {};

        Object.entries(types).forEach(([key, def]) => {
            if (!grouped[def.baseType]) {
                grouped[def.baseType] = [];
            }
            grouped[def.baseType]!.push(key);
        });

        // Sort types within each group by translated name
        Object.keys(grouped).forEach(baseType => {
            grouped[baseType as BaseType]!.sort((a, b) =>
                getDisplayName(a).localeCompare(getDisplayName(b), 'sv')
            );
        });

        return grouped;
    }, [types, getDisplayName]);

    // Auto-expand section and scroll to selected type when loading from URL
    useEffect(() => {
        if (!selectedTypeKey || !types[selectedTypeKey] || hasScrolledToSelection.current) {
            return;
        }

        // Only scroll if this is the type we're waiting for from URL
        if (pendingScrollType.current !== selectedTypeKey) {
            return;
        }

        const typeDef = types[selectedTypeKey];
        const baseType = typeDef.baseType;

        // Expand the section if it's collapsed
        if (collapsedSections.has(baseType)) {
            setCollapsedSections(prev => {
                const next = new Set(prev);
                next.delete(baseType);
                return next;
            });
        }

        // Scroll to the element after a short delay to allow expansion animation
        const scrollTimeout = setTimeout(() => {
            const element = listContainerRef.current?.querySelector(
                `[data-type-key="${selectedTypeKey}"]`
            );
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                hasScrolledToSelection.current = true;
                pendingScrollType.current = null;
            }
        }, 100);

        return () => clearTimeout(scrollTimeout);
    }, [selectedTypeKey, types, collapsedSections]);

    // Group door types by category for sub-grouping in the list
    const doorTypesByCategory = useMemo(() => {
        const doorTypes = typesByBaseType[BaseType.Door] || [];
        const grouped: Record<string, string[]> = {
            [DoorCategory.exterior_door]: [],
            [DoorCategory.interior_door]: [],
            uncategorized: []
        };

        doorTypes.forEach(typeKey => {
            const doorCategory = types[typeKey]?.doorProps?.doorCategory;
            if (doorCategory === DoorCategory.exterior_door) {
                grouped[DoorCategory.exterior_door].push(typeKey);
            } else if (doorCategory === DoorCategory.interior_door) {
                grouped[DoorCategory.interior_door].push(typeKey);
            } else {
                grouped.uncategorized.push(typeKey);
            }
        });

        return grouped;
    }, [typesByBaseType, types]);

    // Handle adding a new component type
    const handleAddType = useCallback(async () => {
        const trimmedName = newTypeName.trim();
        if (!trimmedName) {
            setAddError('Ange ett namn för komponenttypen');
            return;
        }
        if (trimmedName.length > 100) {
            setAddError('Namnet får inte vara längre än 100 tecken');
            return;
        }
        if (!newTypeBaseType) {
            setAddError('Välj en bastyp');
            return;
        }
        // Require category for utility/outdoor types
        const needsCategory = newTypeBaseType === BaseType.Utility || newTypeBaseType === BaseType.Outdoor;
        if (needsCategory && !newTypeCategory) {
            setAddError('Välj en kategori');
            return;
        }

        // Validate custom key if provided
        const trimmedCustomKey = customKeySuffix.trim();
        if (trimmedCustomKey && !isValidCustomKey(trimmedCustomKey)) {
            setAddError('Nyckel får endast innehålla små bokstäver (a-z), siffror och understreck');
            return;
        }

        setIsAdding(true);
        setAddError(null);

        try {
            const db = getDatabase();

            // Generate type key: either custom or auto-generated slug + short ID
            let typeKey: string;
            if (trimmedCustomKey) {
                typeKey = `${newTypeBaseType}_${trimmedCustomKey}`;
            } else {
                const slug = generateSlug(trimmedName, 30);
                const shortId = generateShortId();
                typeKey = `${newTypeBaseType}_${slug}_${shortId}`;
            }

            // Check if key already exists
            if (types[typeKey]) {
                setAddError('En komponenttyp med denna nyckel finns redan');
                setIsAdding(false);
                return;
            }

            const typeData: Record<string, any> = {
                baseType: newTypeBaseType,
                translations: { sv: trimmedName }
            };

            // Include category if required
            if (needsCategory && newTypeCategory) {
                typeData.category = newTypeCategory;
            }

            // Add required props based on baseType for iOS compatibility
            // Note: uvValue and modularSize moved to instance level
            switch (newTypeBaseType) {
                case BaseType.Window:
                    typeData.windowProps = {
                        paneCount: newTypePaneCount || null
                    };
                    break;
                case BaseType.Door:
                    typeData.doorProps = {
                        doorCategory: newTypeDoorCategory || null
                    };
                    break;
                case BaseType.Wall:
                    typeData.surfaceProps = { interiorType: "interior_wall" };
                    break;
                case BaseType.Ceiling:
                    typeData.surfaceProps = { interiorType: "interior_ceiling" };
                    break;
                case BaseType.Floor:
                    typeData.surfaceProps = { interiorType: "interior_floor" };
                    break;
            }

            await set(ref(db, `config/buildComponentTypes/${typeKey}`), typeData);
            recordActivity('settings.updated', { targetKey: typeKey, label: trimmedName });

            await refresh();

            // Reset state and select the new type
            setNewTypeName('');
            setNewTypeCategory('');
            setNewTypePaneCount(null);
            setNewTypeDoorCategory('');
            setCustomKeySuffix('');
            setShowAdvancedSettings(false);
            setShowAddModal(false);
            setSelectedTypeKey(typeKey);
        } catch (error) {
            console.error('Error creating component type:', error);
            setAddError('Kunde inte skapa komponenttypen. Försök igen.');
        } finally {
            setIsAdding(false);
        }
    }, [newTypeName, newTypeBaseType, newTypeCategory, newTypePaneCount, newTypeDoorCategory, customKeySuffix, types, refresh]);

    // Handle closing modal
    const handleCloseModal = useCallback(() => {
        if (!isAdding) {
            setNewTypeName('');
            setNewTypeCategory('');
            setNewTypePaneCount(null);
            setNewTypeDoorCategory('');
            setCustomKeySuffix('');
            setShowAdvancedSettings(false);
            setShowAddModal(false);
            setAddError(null);
        }
    }, [isAdding]);

    // Handle opening modal
    const handleOpenModal = useCallback(() => {
        setShowAddModal(true);
        setNewTypeName('');
        setNewTypeBaseType(null);
        setNewTypeCategory('');
        setNewTypePaneCount(null);
        setNewTypeDoorCategory('');
        setCustomKeySuffix('');
        setShowAdvancedSettings(false);
        setAddError(null);
    }, []);

    // Handle type deletion (called from detail pane)
    const handleTypeDeleted = useCallback(() => {
        setSelectedTypeKey(null);
    }, []);

    // Toggle section collapse
    const toggleSection = useCallback((baseType: BaseType) => {
        setCollapsedSections(prev => {
            const next = new Set(prev);
            if (next.has(baseType)) {
                next.delete(baseType);
            } else {
                next.add(baseType);
            }
            return next;
        });
    }, []);

    // Check if a type has lifetime configured (only requires expectedLifetimeYears)
    const hasLifetime = useCallback((typeKey: string): boolean => {
        const lifetime = types[typeKey]?.maintenance?.lifetime;
        return !!lifetime?.expectedLifetimeYears;
    }, [types]);

    // Check if a type has maintenance templates (from flat structure)
    const hasTemplates = useCallback((typeKey: string): boolean => {
        return getTemplates(typeKey).some(t => !isTemplateArchived(t));
    }, [getTemplates]);

    // Check if a type requires category but is missing it
    const isMissingCategory = useCallback((typeKey: string): boolean => {
        const typeDef = types[typeKey];
        if (!typeDef) return false;
        const requiresCat = typeDef.baseType === BaseType.Utility || typeDef.baseType === BaseType.Outdoor;
        return requiresCat && !typeDef.category;
    }, [types]);

    return (
        <div className="component-types-page-wrapper">
            {/* Tab bar */}
            <div className="component-types-tab-bar">
                <button
                    type="button"
                    className={`component-types-tab ${activeTab === 'types' ? 'active' : ''}`}
                    onClick={() => setActiveTab('types')}
                >
                    Komponenttyper
                </button>
                <button
                    type="button"
                    className={`component-types-tab ${activeTab === 'assessment' ? 'active' : ''}`}
                    onClick={() => setActiveTab('assessment')}
                >
                    Skickbedömning
                </button>
            </div>

            {/* Tab content */}
            {activeTab === 'types' && (
                <div className="component-types-page" ref={typesPageRef} data-mobile-view={mobileView}>
                            {/* Add Type Modal */}
            <Modal show={showAddModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Ny komponenttyp</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Bastyp</Form.Label>
                        <div className="base-type-select-wrapper">
                            {newTypeBaseType && (
                                <img
                                    src={getBaseTypeIconPath(newTypeBaseType)}
                                    alt=""
                                    className="base-type-select-icon"
                                />
                            )}
                            <Form.Select
                                value={newTypeBaseType || ''}
                                onChange={(e) => {
                                    setNewTypeBaseType(e.target.value as BaseType);
                                    setNewTypeCategory(''); // Reset category when base type changes
                                }}
                                className={newTypeBaseType ? 'base-type-select' : ''}
                                disabled={isAdding}
                                autoFocus
                            >
                                <option value="" disabled>Välj bastyp...</option>
                                {[...sortedBaseTypes]
                                    .sort((a, b) => translate(a).localeCompare(translate(b), 'sv'))
                                    .map(bt => (
                                        <option key={bt} value={bt}>
                                            {translate(bt)}
                                        </option>
                                    ))}
                            </Form.Select>
                        </div>
                    </Form.Group>

                    {/* Category field - required for utility/outdoor types */}
                    {requiresCategory && (
                        <Form.Group className="mb-3">
                            <Form.Label>Kategori</Form.Label>
                            <Form.Select
                                value={newTypeCategory}
                                onChange={(e) => setNewTypeCategory(e.target.value)}
                                disabled={isAdding}
                            >
                                <option value="">Välj kategori...</option>
                                {newTypeBaseType === BaseType.Utility &&
                                    Object.values(UtilityCategory)
                                        .sort((a, b) => translate(a).localeCompare(translate(b), 'sv'))
                                        .map(category => (
                                            <option key={category} value={category}>{translate(category)}</option>
                                        ))
                                }
                                {newTypeBaseType === BaseType.Outdoor &&
                                    Object.values(OutdoorCategory)
                                        .sort((a, b) => translate(a).localeCompare(translate(b), 'sv'))
                                        .map(category => (
                                            <option key={category} value={category}>{translate(category)}</option>
                                        ))
                                }
                            </Form.Select>
                        </Form.Group>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Namn</Form.Label>
                        <Form.Control
                            type="text"
                            value={newTypeName}
                            onChange={(e) => setNewTypeName(e.target.value)}
                            placeholder="T.ex. Betongpannor"
                            disabled={isAdding}
                        />
                    </Form.Group>

                    {/* Door category - for door types */}
                    {newTypeBaseType === BaseType.Door && (
                        <Form.Group className="mb-3">
                            <Form.Label>Dörrkategori (valfritt)</Form.Label>
                            <Form.Select
                                value={newTypeDoorCategory}
                                onChange={(e) => setNewTypeDoorCategory(e.target.value)}
                                disabled={isAdding}
                            >
                                <option value="">Ingen kategori</option>
                                <option value="interior_door">Innerdörr</option>
                                <option value="exterior_door">Ytterdörr</option>
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Bestämmer vilken sektion dörrtypen visas under i appen.
                            </Form.Text>
                        </Form.Group>
                    )}

                    {/* Pane count - for window types */}
                    {newTypeBaseType === BaseType.Window && (
                        <Form.Group className="mb-3">
                            <Form.Label>Antal glas (valfritt)</Form.Label>
                            <Form.Select
                                value={newTypePaneCount ?? ''}
                                onChange={(e) => setNewTypePaneCount(e.target.value ? parseInt(e.target.value) : null)}
                                disabled={isAdding}
                            >
                                <option value="">Ej specificerat</option>
                                <option value="1">1-glas</option>
                                <option value="2">2-glas</option>
                                <option value="3">3-glas</option>
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Visas som metadata på fönstertypen i appen.
                            </Form.Text>
                        </Form.Group>
                    )}

                    {/* Advanced settings toggle */}
                    <div
                        className="advanced-settings-toggle mb-2"
                        onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                        role="button"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            cursor: 'pointer',
                            color: '#6c757d',
                            fontSize: '0.875rem'
                        }}
                    >
                        <Settings size={14} />
                        <span>Avancerade inställningar</span>
                        {showAdvancedSettings ? (
                            <ChevronDown size={14} />
                        ) : (
                            <ChevronRight size={14} />
                        )}
                    </div>

                    {/* Advanced settings content */}
                    <Collapse in={showAdvancedSettings}>
                        <div>
                            <Form.Group className="mb-3">
                                <Form.Label>Anpassad nyckel</Form.Label>
                                <div className="d-flex align-items-center">
                                    <span
                                        className="text-muted me-1"
                                        style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                                    >
                                        {newTypeBaseType || 'bastyp'}_
                                    </span>
                                    <Form.Control
                                        type="text"
                                        value={customKeySuffix}
                                        onChange={(e) => setCustomKeySuffix(e.target.value.toLowerCase())}
                                        placeholder="din_nyckel"
                                        disabled={isAdding}
                                        style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                                    />
                                </div>
                                <Form.Text className="text-muted">
                                    Lämna tomt för automatisk generering. Endast små bokstäver (a-z), siffror och understreck.
                                </Form.Text>
                            </Form.Group>
                        </div>
                    </Collapse>

                    {addError && (
                        <div className="alert alert-danger py-2 mb-0">{addError}</div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal} disabled={isAdding}>
                        Avbryt
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAddType}
                        disabled={isAdding || !newTypeName.trim() || !newTypeBaseType || (requiresCategory && !newTypeCategory)}
                    >
                        {isAdding ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Skapar...
                            </>
                        ) : (
                            'Skapa'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Left Pane - Type List */}
            <div className="component-types-left-pane" onClickCapture={rememberListScroll}>
                {/* Header with create button */}
                <div className="left-pane-header">
                    <Button variant="primary" size="sm" onClick={handleOpenModal}>
                        <Plus size={16} className="me-1" />
                        Skapa ny
                    </Button>
                </div>

                {/* Type list */}
                <div className="type-list-container" ref={listContainerRef}>
                    {sortedBaseTypes.map(baseType => {
                        const typesForBase = typesByBaseType[baseType] || [];
                        const typeCount = typesForBase.length;
                        const isCollapsed = collapsedSections.has(baseType);

                        // Skip empty sections
                        if (typeCount === 0) return null;

                        return (
                            <div key={baseType} className="base-type-section">
                                {/* Section header - clickable to toggle */}
                                <div
                                    className="base-type-header"
                                    onClick={() => toggleSection(baseType)}
                                    role="button"
                                    aria-expanded={!isCollapsed}
                                >
                                    {isCollapsed ? (
                                        <ChevronRight size={16} className="base-type-chevron" />
                                    ) : (
                                        <ChevronDown size={16} className="base-type-chevron" />
                                    )}
                                    <img
                                        src={getBaseTypeIconPath(baseType)}
                                        alt=""
                                        className="base-type-header-icon"
                                    />
                                    <span className="base-type-header-name">{translate(baseType)}</span>
                                    <span className="badge bg-secondary">{typeCount}</span>
                                </div>

                                {/* Collapsible type list */}
                                <Collapse in={!isCollapsed}>
                                    <div className="type-list">
                                        {baseType === BaseType.Door ? (
                                            // Door types are sub-grouped by category
                                            <>
                                                {doorTypesByCategory[DoorCategory.exterior_door].length > 0 && (
                                                    <div className="door-category-group">
                                                        <div className="door-category-header">Ytterdörrar</div>
                                                        {doorTypesByCategory[DoorCategory.exterior_door].map(typeKey => (
                                                            <div
                                                                key={typeKey}
                                                                data-type-key={typeKey}
                                                                className={`type-item ${selectedTypeKey === typeKey ? 'selected' : ''}`}
                                                                onClick={() => setSelectedTypeKey(typeKey)}
                                                            >
                                                                <ComponentTypeIcon
                                                                    componentType={typeKey}
                                                                    buildComponentTypes={types}
                                                                    className="type-item-icon"
                                                                    alt={getTranslation(typeKey)}
                                                                />
                                                                <span className="type-item-name">{getDisplayName(typeKey)}</span>
                                                                <div className="type-item-status">
                                                                    <span className="status-slot" title={hasTemplates(typeKey) ? "Underhållsmallar konfigurerade" : undefined}>
                                                                        {hasTemplates(typeKey) && <Wrench size={14} className="status-icon status-templates" />}
                                                                    </span>
                                                                    <span className="status-slot" title={hasLifetime(typeKey) ? "Livslängd konfigurerad" : undefined}>
                                                                        {hasLifetime(typeKey) && <Clock size={14} className="status-icon status-lifetime" />}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                {doorTypesByCategory[DoorCategory.interior_door].length > 0 && (
                                                    <div className="door-category-group">
                                                        <div className="door-category-header">Innerdörrar</div>
                                                        {doorTypesByCategory[DoorCategory.interior_door].map(typeKey => (
                                                            <div
                                                                key={typeKey}
                                                                data-type-key={typeKey}
                                                                className={`type-item ${selectedTypeKey === typeKey ? 'selected' : ''}`}
                                                                onClick={() => setSelectedTypeKey(typeKey)}
                                                            >
                                                                <ComponentTypeIcon
                                                                    componentType={typeKey}
                                                                    buildComponentTypes={types}
                                                                    className="type-item-icon"
                                                                    alt={getTranslation(typeKey)}
                                                                />
                                                                <span className="type-item-name">{getDisplayName(typeKey)}</span>
                                                                <div className="type-item-status">
                                                                    <span className="status-slot" title={hasTemplates(typeKey) ? "Underhållsmallar konfigurerade" : undefined}>
                                                                        {hasTemplates(typeKey) && <Wrench size={14} className="status-icon status-templates" />}
                                                                    </span>
                                                                    <span className="status-slot" title={hasLifetime(typeKey) ? "Livslängd konfigurerad" : undefined}>
                                                                        {hasLifetime(typeKey) && <Clock size={14} className="status-icon status-lifetime" />}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                {doorTypesByCategory.uncategorized.length > 0 && (
                                                    <div className="door-category-group">
                                                        <div className="door-category-header">Okategoriserade</div>
                                                        {doorTypesByCategory.uncategorized.map(typeKey => (
                                                            <div
                                                                key={typeKey}
                                                                data-type-key={typeKey}
                                                                className={`type-item ${selectedTypeKey === typeKey ? 'selected' : ''}`}
                                                                onClick={() => setSelectedTypeKey(typeKey)}
                                                            >
                                                                <ComponentTypeIcon
                                                                    componentType={typeKey}
                                                                    buildComponentTypes={types}
                                                                    className="type-item-icon"
                                                                    alt={getTranslation(typeKey)}
                                                                />
                                                                <span className="type-item-name">{getDisplayName(typeKey)}</span>
                                                                <div className="type-item-status">
                                                                    <span className="status-slot" title="Dörrkategori saknas">
                                                                        <AlertTriangle size={14} className="status-icon status-warning" />
                                                                    </span>
                                                                    <span className="status-slot" title={hasTemplates(typeKey) ? "Underhållsmallar konfigurerade" : undefined}>
                                                                        {hasTemplates(typeKey) && <Wrench size={14} className="status-icon status-templates" />}
                                                                    </span>
                                                                    <span className="status-slot" title={hasLifetime(typeKey) ? "Livslängd konfigurerad" : undefined}>
                                                                        {hasLifetime(typeKey) && <Clock size={14} className="status-icon status-lifetime" />}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            // Other base types use flat list
                                            typesForBase.map(typeKey => (
                                                <div
                                                    key={typeKey}
                                                    data-type-key={typeKey}
                                                    className={`type-item ${selectedTypeKey === typeKey ? 'selected' : ''}`}
                                                    onClick={() => setSelectedTypeKey(typeKey)}
                                                >
                                                    <ComponentTypeIcon
                                                        componentType={typeKey}
                                                        buildComponentTypes={types}
                                                        className="type-item-icon"
                                                        alt={getTranslation(typeKey)}
                                                    />
                                                    <span className="type-item-name">{getDisplayName(typeKey)}</span>
                                                    <div className="type-item-status">
                                                        <span className="status-slot" title={isMissingCategory(typeKey) ? "Kategori saknas" : undefined}>
                                                            {isMissingCategory(typeKey) && <AlertTriangle size={14} className="status-icon status-warning" />}
                                                        </span>
                                                        <span className="status-slot" title={hasTemplates(typeKey) ? "Underhållsmallar konfigurerade" : undefined}>
                                                            {hasTemplates(typeKey) && <Wrench size={14} className="status-icon status-templates" />}
                                                        </span>
                                                        <span className="status-slot" title={hasLifetime(typeKey) ? "Livslängd konfigurerad" : undefined}>
                                                            {hasLifetime(typeKey) && <Clock size={14} className="status-icon status-lifetime" />}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </Collapse>
                            </div>
                        );
                    })}
                </div>
            </div>

                    {/* Right Pane - Detail/Edit */}
                    <div className="component-types-right-pane">
                        {mobileView === 'detail' && (
                            <button type="button" className="component-types-back-button" onClick={() => setSelectedTypeKey(null)}>
                                <ChevronLeft size={20} aria-hidden="true" />
                                Alla komponenttyper
                            </button>
                        )}
                        {selectedTypeKey ? (
                            <ComponentTypeDetailPane
                                typeKey={selectedTypeKey}
                                onDeleted={handleTypeDeleted}
                            />
                        ) : (
                            <div className="empty-state">
                                <div className="text-muted">
                                    Välj en komponenttyp från listan för att visa och redigera detaljer
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'assessment' && (
                <ConditionAssessmentTab />
            )}
        </div>
    );
};

export default ComponentTypesManagementPage;
